import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { FloppyDisk, Heart, Moon, Lightning, Smiley, Bed, Pulse, Trash } from "@phosphor-icons/react";
import { LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";

// Get Monday of current week as YYYY-MM-DD (local)
function getMondayOf(date = new Date()) {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun, 1=Mon
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

const QUESTIONS = [
  { key: "sleep", label: "Sueño", icon: Moon, lowText: "Muy malo", highText: "Excelente" },
  { key: "mood", label: "Humor", icon: Smiley, lowText: "Muy malo", highText: "Excelente" },
  { key: "rest", label: "Descanso", icon: Bed, lowText: "Insuficiente", highText: "Completo" },
  { key: "stress", label: "Estrés", icon: Lightning, lowText: "Muy alto", highText: "Casi nada" },
  { key: "fatigue", label: "Fatiga", icon: Pulse, lowText: "Muy fatigado", highText: "Sin fatiga" },
];

function scoreColor(score) {
  // score out of 25
  if (score >= 20) return "#32D74B";
  if (score >= 15) return "#FFD60A";
  if (score >= 10) return "#FF9500";
  return "#FF3B30";
}
function scoreLabel(score) {
  if (score >= 20) return "Óptimo";
  if (score >= 15) return "Bueno";
  if (score >= 10) return "Regular";
  return "Bajo";
}

export default function WellnessTab({ userId, isOwner, isAdmin }) {
  const canSubmit = isOwner; // Solo el propio atleta puede completar su cuestionario
  const thisMonday = useMemo(() => getMondayOf(), []);
  const today = new Date();
  const isMonday = today.getDay() === 1;
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ sleep: 3, stress: 3, mood: 3, rest: 3, fatigue: 3, notes: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/users/${userId}/wellness`);
      setEntries(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const currentEntry = entries.find((e) => e.week_of === thisMonday);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/users/${userId}/wellness`, {
        week_of: thisMonday,
        sleep: form.sleep,
        stress: form.stress,
        mood: form.mood,
        rest: form.rest,
        fatigue: form.fatigue,
        notes: form.notes,
      });
      toast.success("Cuestionario guardado");
      setForm({ sleep: 3, stress: 3, mood: 3, rest: 3, fatigue: 3, notes: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta entrada?")) return;
    try {
      await api.delete(`/wellness/${id}`);
      toast.success("Eliminada");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const chartData = useMemo(() => {
    return [...entries]
      .sort((a, b) => a.week_of.localeCompare(b.week_of))
      .map((e) => ({
        week: e.week_of,
        Sueño: e.sleep,
        Humor: e.mood,
        Descanso: e.rest,
        Estrés: e.stress,
        Fatiga: e.fatigue,
        Promedio: +e.avg,
      }));
  }, [entries]);

  if (loading) return <div className="text-muted">Cargando...</div>;

  return (
    <div className="space-y-6">
      {/* Weekly form */}
      <div className="card-base p-6 sm:p-8" data-testid="wellness-form-card">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Heart size={22} weight="duotone" className="text-primary-app" />
              <h2 className="font-display font-black uppercase text-2xl tracking-tight">Cuestionario Wellness</h2>
            </div>
            <p className="text-muted text-sm">
              Semana del <span className="text-white font-semibold">{thisMonday}</span> · Completar los días lunes
            </p>
          </div>
          {!isMonday && !currentEntry && (
            <div className="text-xs uppercase tracking-widest text-[#FFD60A] border border-[#FFD60A] px-3 py-1.5" data-testid="not-monday-warn">
              ⚠ Hoy no es lunes — Idealmente completar el lunes
            </div>
          )}
        </div>

        {currentEntry ? (
          <div className="space-y-4" data-testid="wellness-submitted">
            <div className="flex items-center justify-between flex-wrap gap-3 p-4 bg-[#0A0A0A] border border-app">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted mb-1">Índice wellness de esta semana</div>
                <div className="flex items-baseline gap-3">
                  <span className="font-display font-black text-5xl" style={{ color: scoreColor(currentEntry.score) }}>
                    {currentEntry.score}
                  </span>
                  <span className="text-muted">/ 25</span>
                  <span className="font-display font-bold uppercase" style={{ color: scoreColor(currentEntry.score) }}>
                    {scoreLabel(currentEntry.score)}
                  </span>
                </div>
              </div>
              {isAdmin && (
                <button onClick={() => handleDelete(currentEntry.wellness_id)} className="btn-secondary !py-2 !px-3 hover:!border-[#FF3B30]" title="Eliminar">
                  <Trash size={14} weight="bold" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {QUESTIONS.map((q) => (
                <div key={q.key} className="bg-[#0A0A0A] border border-app p-4 text-center">
                  <q.icon size={24} weight="duotone" className="text-primary-app mx-auto mb-1" />
                  <div className="text-[10px] uppercase tracking-widest text-muted mb-1">{q.label}</div>
                  <div className="font-display font-black text-3xl">{currentEntry[q.key]}<span className="text-muted text-base">/5</span></div>
                </div>
              ))}
            </div>
            {currentEntry.notes && (
              <div className="text-sm text-muted border-l-2 border-[#FF3B30] pl-3 py-1">{currentEntry.notes}</div>
            )}
          </div>
        ) : canSubmit ? (
          <form onSubmit={submit} className="space-y-5" data-testid="wellness-form">
            <p className="text-sm text-muted">Puntuá cada ítem del 1 al 5 — <span className="text-white">1 = malo/bajo</span>, <span className="text-white">5 = excelente/alto</span></p>

            {QUESTIONS.map((q) => (
              <div key={q.key} className="card-base !border-app p-4" data-testid={`wellness-q-${q.key}`}>
                <div className="flex items-center gap-2 mb-3">
                  <q.icon size={20} weight="duotone" className="text-primary-app" />
                  <span className="font-display font-bold uppercase text-base tracking-tight">{q.label}</span>
                </div>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setForm({ ...form, [q.key]: n })}
                      className={`flex-1 aspect-square sm:aspect-auto sm:py-3 font-display font-black text-xl border transition-all ${
                        form[q.key] === n
                          ? "border-white bg-[#FF3B30] text-white"
                          : "border-[#262626] text-muted hover:text-white hover:border-white"
                      }`}
                      data-testid={`wellness-${q.key}-${n}`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <div className="flex justify-between text-[10px] uppercase tracking-widest text-muted mt-2">
                  <span>1 · {q.lowText}</span>
                  <span>5 · {q.highText}</span>
                </div>
              </div>
            ))}

            <div>
              <label className="text-xs uppercase tracking-widest text-muted block mb-2">Notas (opcional)</label>
              <textarea
                className="input-base"
                rows="2"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Ej: Semana con examen, dormí poco..."
                data-testid="wellness-notes"
              />
            </div>

            <button type="submit" disabled={saving} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="wellness-submit">
              <FloppyDisk size={18} weight="bold" />
              {saving ? "Guardando..." : `Guardar cuestionario (${form.sleep + form.stress + form.mood + form.rest + form.fatigue}/25)`}
            </button>
          </form>
        ) : (
          <div className="text-muted text-sm">El atleta aún no completó el cuestionario de esta semana.</div>
        )}
      </div>

      {/* History + chart */}
      {entries.length > 0 && (
        <div className="card-base p-6" data-testid="wellness-chart">
          <h3 className="font-display font-black uppercase text-xl mb-4 tracking-tight">Evolución semanal</h3>
          {entries.length < 2 ? (
            <p className="text-muted text-sm">Completá al menos 2 cuestionarios para ver la evolución.</p>
          ) : (
            <div className="w-full" style={{ height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid stroke="#262626" strokeDasharray="2 4" />
                  <XAxis dataKey="week" stroke="#A3A3A3" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#A3A3A3" tick={{ fontSize: 11 }} domain={[0, 5]} />
                  <Tooltip
                    contentStyle={{ background: "#141414", border: "1px solid #262626", borderRadius: 0, color: "#fff" }}
                    labelStyle={{ color: "#A3A3A3", textTransform: "uppercase", letterSpacing: 2, fontSize: 11 }}
                  />
                  <ReferenceLine y={3} stroke="#FFD60A" strokeDasharray="3 3" label={{ value: "Umbral", fill: "#FFD60A", fontSize: 10 }} />
                  <Line type="monotone" dataKey="Sueño" stroke="#007AFF" strokeWidth={1.5} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="Humor" stroke="#32D74B" strokeWidth={1.5} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="Descanso" stroke="#AF52DE" strokeWidth={1.5} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="Estrés" stroke="#FF9500" strokeWidth={1.5} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="Fatiga" stroke="#FFD60A" strokeWidth={1.5} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="Promedio" stroke="#FF3B30" strokeWidth={3} dot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="mt-6 space-y-2">
            {entries.map((e) => (
              <div key={e.wellness_id} className="flex items-center justify-between p-3 bg-[#0A0A0A] border border-app">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="font-display font-black text-2xl" style={{ color: scoreColor(e.score) }}>
                    {e.score}
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-widest text-muted">Semana del {e.week_of}</div>
                    <div className="text-xs text-muted truncate">
                      Sueño {e.sleep} · Humor {e.mood} · Desc {e.rest} · Estrés {e.stress} · Fatiga {e.fatigue}
                    </div>
                  </div>
                </div>
                {isAdmin && (
                  <button onClick={() => handleDelete(e.wellness_id)} className="text-muted hover:text-[#FF3B30]">
                    <Trash size={14} weight="bold" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
